import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import { GET_ALL_INVOICES } from '../graphql/query';
import { DELETE_INVOICE, SEND_INVOICE_EMAIL, UPDATE_INVOICE_PAID_STATUS } from '../graphql/mutation';
import { Invoice, InvoicesData } from '../types/interfaces';
import { PageHeader, StatusBadge, CurrencyDisplay, ConfirmDialog, EmptyState } from '../components/common';
import { getInvoiceStatus } from '../utils/invoiceStatus';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CreateInvoiceDialog from '../components/CreateInvoiceDialog';
import InvoiceDetailDialog from '../components/InvoiceDetailDialog';
import { useSnackbar } from 'notistack';

const InvoiceListPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; id: string | null }>({
    open: false,
    id: null,
  });
  const [confirmPaid, setConfirmPaid] = useState<{ open: boolean; id: string | null }>({
    open: false,
    id: null,
  });
  const [sendEmailDialog, setSendEmailDialog] = useState<{ open: boolean; invoice: Invoice | null }>({
    open: false,
    invoice: null,
  });
  const [emailAddress, setEmailAddress] = useState('');
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);

  const { enqueueSnackbar } = useSnackbar();
  const { loading, error, data, refetch } = useQuery<InvoicesData>(GET_ALL_INVOICES);
  const [deleteInvoice] = useMutation(DELETE_INVOICE, {
    refetchQueries: [{ query: GET_ALL_INVOICES }],
  });
  const [updateInvoicePaidStatus] = useMutation(UPDATE_INVOICE_PAID_STATUS, {
    refetchQueries: [{ query: GET_ALL_INVOICES }],
  });
  const [sendInvoiceEmail, { loading: sendingEmail }] = useMutation(SEND_INVOICE_EMAIL);

  const handleDelete = async () => {
    if (!confirmDelete.id) return;
    try {
      await deleteInvoice({ variables: { id: confirmDelete.id } });
      enqueueSnackbar('Invoice deleted successfully', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Failed to delete invoice', { variant: 'error' });
    } finally {
      setConfirmDelete({ open: false, id: null });
    }
  };

  const handleMarkPaid = async () => {
    if (!confirmPaid.id) return;
    try {
      await updateInvoicePaidStatus({ variables: { id: confirmPaid.id } });
      enqueueSnackbar('Invoice marked as paid', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Failed to update invoice', { variant: 'error' });
    } finally {
      setConfirmPaid({ open: false, id: null });
    }
  };

  const handleOpenSendEmail = (invoice: Invoice) => {
    setSendEmailDialog({ open: true, invoice });
    setEmailAddress(invoice.customerEmail || '');
  };

  const handleSendEmail = async () => {
    if (!sendEmailDialog.invoice) return;
    try {
      await sendInvoiceEmail({
        variables: {
          invoiceId: sendEmailDialog.invoice.id,
          email: emailAddress || undefined,
        },
      });
      enqueueSnackbar('Invoice email sent successfully', { variant: 'success' });
      setSendEmailDialog({ open: false, invoice: null });
      setEmailAddress('');
      refetch();
    } catch (err) {
      enqueueSnackbar(
        err instanceof Error ? err.message : 'Failed to send invoice email',
        { variant: 'error' }
      );
    }
  };

  const handleInvoiceCreated = () => {
    setDialogOpen(false);
    enqueueSnackbar('Invoice created successfully!', { variant: 'success' });
    refetch();
  };

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-AU');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={10}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center" sx={{ p: 2 }}>
        Error loading invoices: {error.message}
      </Typography>
    );
  }

  const invoices = data?.invoices || [];
  const paginatedInvoices = invoices.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <>
      <PageHeader
        title="Invoices"
        subtitle={`${invoices.length} invoice${invoices.length !== 1 ? 's' : ''}`}
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            New Invoice
          </Button>
        }
      />

      {invoices.length === 0 ? (
        <EmptyState
          icon={<ReceiptLongIcon sx={{ fontSize: 64 }} />}
          title="No invoices yet"
          description="Create your first invoice to get started tracking your revenue."
          action={{ label: 'Create Invoice', onClick: () => setDialogOpen(true) }}
        />
      ) : (
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedInvoices.map((invoice) => (
                  <TableRow
                    key={invoice.id}
                    hover
                    onClick={() => setSelectedInvoiceId(invoice.id)}
                    sx={{ '&:last-child td': { borderBottom: 0 }, cursor: 'pointer' }}
                  >
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {invoice.invoiceNumber || `#${invoice.id.substring(0, 8).toUpperCase()}`}
                    </TableCell>
                    <TableCell>
                      {formatDate(invoice.invoiceDate)}
                    </TableCell>
                    <TableCell>{invoice.customerName}</TableCell>
                    <TableCell align="right">
                      <CurrencyDisplay amount={invoice.amount} />
                    </TableCell>
                    <TableCell align="center">
                      <StatusBadge
                        status={getInvoiceStatus(invoice)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Send invoice via email">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleOpenSendEmail(invoice)}
                          sx={{ mr: 0.5 }}
                        >
                          <EmailIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!invoice.paid && (
                        <Tooltip title="Mark as paid">
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            sx={{ mr: 0.5, minWidth: 0, px: 1 }}
                            onClick={() => setConfirmPaid({ open: true, id: invoice.id })}
                          >
                            Paid
                          </Button>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete invoice">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => setConfirmDelete({ open: true, id: invoice.id })}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={invoices.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      )}

      <InvoiceDetailDialog
        invoiceId={selectedInvoiceId}
        open={!!selectedInvoiceId}
        onClose={() => setSelectedInvoiceId(null)}
      />

      <CreateInvoiceDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onInvoiceCreated={handleInvoiceCreated}
        onError={(msg) => enqueueSnackbar(msg, { variant: 'error' })}
      />

      {/* Send Email Dialog */}
      <Dialog
        open={sendEmailDialog.open}
        onClose={() => setSendEmailDialog({ open: false, invoice: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Send Invoice via Email</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Send invoice{' '}
            <strong>
              {sendEmailDialog.invoice?.invoiceNumber ||
                sendEmailDialog.invoice?.id.substring(0, 8).toUpperCase()}
            </strong>{' '}
            to the customer via email.
          </Typography>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={emailAddress}
            onChange={(e) => setEmailAddress(e.target.value)}
            placeholder="customer@example.com"
            helperText="Leave blank to use the email on file"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setSendEmailDialog({ open: false, invoice: null });
              setEmailAddress('');
            }}
            color="secondary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendEmail}
            variant="contained"
            disabled={sendingEmail}
            startIcon={sendingEmail ? <CircularProgress size={18} color="inherit" /> : <EmailIcon />}
          >
            {sendingEmail ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmDelete.open}
        title="Delete Invoice"
        message="Are you sure you want to delete this invoice? This action cannot be undone."
        severity="error"
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete({ open: false, id: null })}
      />

      <ConfirmDialog
        open={confirmPaid.open}
        title="Mark as Paid"
        message="Are you sure you want to mark this invoice as paid?"
        severity="warning"
        confirmLabel="Mark Paid"
        onConfirm={handleMarkPaid}
        onCancel={() => setConfirmPaid({ open: false, id: null })}
      />
    </>
  );
};

export default InvoiceListPage;
